import React, { useEffect, useState } from 'react';
import {
	Alert,
	Button,
	Divider,
	Input,
	Modal,
	Popconfirm,
	Select,
	Space,
	Table,
	Tabs,
	Tag,
	Tooltip,
	Typography,
	message,
} from 'antd';
import { DeleteOutlined, EditOutlined, FilePdfOutlined, PlusOutlined } from '@ant-design/icons';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import {
	createPublication,
	createPublicationNotification,
	deletePublication,
	deletePublicationNotification,
	getPublicationCrawlRuns,
	getPublicationNotifications,
	getPublications,
	updatePublication,
	updatePublicationNotification,
} from '../api';
import { SOURCE_OPTIONS, SOURCE_LABELS, SOURCES } from '../utils/constants';
import { getPublicationFileName } from '../utils/helpers';
import PublicationModal from '../components/PublicationModal';
import NotificationModal from '../components/NotificationModal';

const { Search } = Input;
const { TabPane } = Tabs;
const { Text } = Typography;

const PublicationsWrapper = styled.div`
	width: 100%;
	height: 100%;
`;

const Toolbar = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
`;

const ToolbarRight = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-left: auto;
`;

const sourceLabel = (source: string) => SOURCE_LABELS[source] || (source || '').toUpperCase();

const formatDate = (value: string | null, withTime = false) => {
	if (!value) {
		return '—';
	}

	const parsed = moment(value);
	if (!parsed.isValid()) {
		return value;
	}

	return parsed.format(withTime ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY');
};

const statusTag = (status: string) => {
	const colors: Record<string, string> = {
		completed: 'green',
		failed: 'red',
		running: 'blue',
		pending: 'gold',
	};

	return <Tag color={colors[status] || 'default'}>{status || 'unknown'}</Tag>;
};

function latestRunForSource(runs: any[], source: string) {
	return runs.find((run) => run.source === source);
}

function Publications() {
	const [loading, setLoading]: any = useState(false);
	const [publications, setPublications]: any = useState([]);
	const [publicationsTotal, setPublicationsTotal]: any = useState(0);
	const [crawlRuns, setCrawlRuns]: any = useState([]);
	const [crawlRunsTotal, setCrawlRunsTotal]: any = useState(0);
	const [unavailable, setUnavailable]: any = useState(false);
	const [publicationSearch, setPublicationSearch]: any = useState('');
	const [publicationSource, setPublicationSource]: any = useState('');
	const [publicationsPage, setPublicationsPage]: any = useState(1);
	const [crawlRunsPage, setCrawlRunsPage]: any = useState(1);
	const [showPreview, setShowPreview]: any = useState(false);
	const [crawlRunsSource, setCrawlRunsSource]: any = useState('');
	const [publicationModalVisible, setPublicationModalVisible]: any = useState(false);
	const [selectedPublication, setSelectedPublication]: any = useState({});
	const [savingPublication, setSavingPublication]: any = useState(false);
	const [notifications, setNotifications]: any = useState([]);
	const [savingNotification, setSavingNotification]: any = useState(false);
	const [notificationsModalVisible, setNotificationsModalVisible]: any = useState(false);
	const [selectedNotification, setSelectedNotification]: any = useState({});

	async function fetchPublications() {
		setLoading(true);
		setUnavailable(false);
		try {
			const publicationsResp = await getPublications({
				source: publicationSource || undefined,
				search: publicationSearch || undefined,
				page: publicationsPage,
			});

			setPublications(publicationsResp?.publications || []);
			setPublicationsTotal(publicationsResp?.total || 0);
		} catch (err) {
			setUnavailable(true);
			setPublications([]);
			message.error('Cannot load publications. Is the publications service running?');
		}

		setLoading(false);
	}

	async function fetchCrawlRuns() {
		setLoading(true);
		setUnavailable(false);
		try {
			const crawlRunsResp = await getPublicationCrawlRuns({ source: crawlRunsSource, page: crawlRunsPage });

			setCrawlRuns(crawlRunsResp?.crawl_runs || []);
			setCrawlRunsTotal(crawlRunsResp?.total || 0);
		} catch (err) {
			setUnavailable(true);
			setCrawlRuns([]);
			message.error('Cannot load publication crawl runs. Is the publications service running?');
		}

		setLoading(false);
	}

	async function fetchNotifications() {
		setLoading(true);
		setUnavailable(false);
		try {
			const notificationsResp = await getPublicationNotifications();

			setNotifications(notificationsResp?.notifications || []);
		} catch (err) {
			setUnavailable(true);
			setNotifications([]);
			message.error('Cannot load publication notifications. Is the publications service running?');
		}

		setLoading(false);
	}

	async function handleSaveNotification(values: any) {
		setSavingNotification(true);
		try {
			if (values?.id) {
				await updatePublicationNotification(values.id, values);
			} else {
				await createPublicationNotification(values);
			}
			fetchNotifications();
			setSelectedNotification({});
			setNotificationsModalVisible(false);
		} catch (err) {
			message.error('Failed to save notification. Some error occurred.');
		}
		setSavingNotification(false);
	}

	async function handleDeleteNotification(notificationId: string) {
		try {
			await deletePublicationNotification(notificationId);
			fetchNotifications();
			setSelectedNotification({});
		} catch (err) {
			message.error('Failed to delete notification. Some error occurred.');
		}
	}

	async function handleSavePublication(values: any) {
		setSavingPublication(true);
		try {
			if (selectedPublication?.id) {
				await updatePublication(selectedPublication.id, values);
			} else {
				await createPublication(values);
			}
			fetchPublications();
			setPublicationModalVisible(false);
		} catch (err) {
			message.error('Failed to save publication. Some error occurred.');
		}
		setSavingPublication(false);
	}

	async function handleDeletePublication(publicationId: string) {
		try {
			await deletePublication(publicationId);
			fetchPublications();
		} catch (err) {
			message.error('Failed to delete publication. Some error occurred.');
		}
	}

	useEffect(() => {
		fetchPublications();
	}, [publicationSource, publicationSearch, publicationsPage]);

	useEffect(() => {
		fetchCrawlRuns();
	}, [crawlRunsSource, crawlRunsPage]);

	useEffect(() => {
		fetchNotifications();
	}, []);

	const failedSources = SOURCES.filter((source) => latestRunForSource(crawlRuns, source)?.status === 'failed');

	const publicationColumns = [
		{
			title: 'Source',
			dataIndex: 'source',
			key: 'source',
			width: 100,
			render: (value: string) => sourceLabel(value),
		},
		{
			title: 'Number',
			dataIndex: 'number',
			key: 'number',
			width: 160,
			render: (value: string, record: any) => (
				<Typography.Text strong>
					<a href={record.detail_url} target="_blank" rel="noopener noreferrer">
						{value}
					</a>
				</Typography.Text>
			),
		},
		{
			title: 'Description',
			dataIndex: 'description',
			key: 'description',
			ellipsis: true,
		},
		{
			title: 'Manufacturer',
			dataIndex: 'manufacturer',
			key: 'manufacturer',
			render: (value: string, record: any) => (
				<Typography.Text ellipsis={{ tooltip: record.manufacturer ? record.manufacturer.join(', ') : '' }}>
					{record.manufacturer ? record.manufacturer.join(', ') : ''}
				</Typography.Text>
			),
		},
		{
			title: 'Model',
			dataIndex: 'model',
			key: 'model',
			render: (value: string, record: any) => (
				<Typography.Text ellipsis={{ tooltip: record.model ? record.model.join(', ') : '' }}>
					{record.model ? record.model.join(', ') : ''}
				</Typography.Text>
			),
		},
		{
			title: 'Type',
			dataIndex: 'publication_type',
			key: 'publication_type',
			width: 120,
		},
		{
			title: 'Issued by',
			dataIndex: 'issued_by',
			key: 'issued_by',
			width: 120,
		},
		{
			title: 'Issue date',
			dataIndex: 'issue_date',
			key: 'issue_date',
			width: 130,
			render: (value: string) => formatDate(value),
		},
		{
			title: 'Effective date',
			dataIndex: 'effective_date',
			key: 'effective_date',
			width: 130,
			render: (value: string) => formatDate(value),
		},
		{
			title: '',
			key: 'actions',
			width: 140,
			render: (_text: string, record: any) => (
				<Space>
					<a
						onClick={(e) => {
							if (record?.pdf_s3_keys.length) {
								e.preventDefault();
								setSelectedPublication(record);
								setShowPreview(true);
							}
						}}
					>
						<Tooltip title={record?.pdf_s3_keys.length ? 'Preview Files' : 'File not available'}>
							<span style={{ display: 'inline-flex', alignItems: 'center' }}>
								<FilePdfOutlined style={{ color: record?.pdf_s3_keys.length ? undefined : 'red' }} />
							</span>
						</Tooltip>
					</a>
					<Divider type="vertical" />
					<a
						onClick={() => {
							setSelectedPublication(record);
							setPublicationModalVisible(true);
						}}
					>
						<Tooltip title="Edit">
							<EditOutlined />
						</Tooltip>
					</a>
					<Divider type="vertical" />
					<a>
						<Popconfirm
							placement="topRight"
							title={`Delete ${record.number}?`}
							onConfirm={async () => {
								await handleDeletePublication(record.id);
							}}
							okText="Yes"
							cancelText="No"
						>
							<Tooltip title="Delete">
								<DeleteOutlined />
							</Tooltip>
						</Popconfirm>
					</a>
				</Space>
			),
		},
	];

	const crawlRunColumns = [
		{
			title: 'Started',
			dataIndex: 'started_at',
			key: 'started_at',
			width: 160,
			render: (value: string) => formatDate(value, true),
		},
		{
			title: 'Source',
			dataIndex: 'source',
			key: 'source',
			width: 100,
			render: (value: string) => sourceLabel(value),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			width: 120,
			render: (value: string) => statusTag(value),
		},
		{
			title: 'Found',
			dataIndex: 'found_count',
			key: 'found_count',
			width: 90,
		},
		{
			title: 'New',
			dataIndex: 'new_count',
			key: 'new_count',
			width: 90,
			render: (value: number, record: any) => {
				if (Number(value) > 0) {
					return <Text strong>{value} new</Text>;
				}

				if (record.status === 'completed') {
					return <Text type="secondary">none</Text>;
				}

				return value;
			},
		},
		{
			title: 'Finished',
			dataIndex: 'finished_at',
			key: 'finished_at',
			width: 160,
			render: (value: string) => formatDate(value, true),
		},
		{
			title: 'Error',
			dataIndex: 'error_message',
			key: 'error_message',
			ellipsis: true,
			render: (value: string) =>
				value ? (
					<Text type="danger" ellipsis={{ tooltip: value }}>
						{value}
					</Text>
				) : (
					'—'
				),
		},
	];

	const notificationColumns = [
		{
			title: 'Title',
			dataIndex: 'title',
			key: 'title',
		},
		{
			title: 'Description',
			dataIndex: 'description',
			key: 'description',
		},
		{
			title: 'Action link',
			dataIndex: 'action_link',
			key: 'action_link',
		},
		{
			title: '',
			key: 'actions',
			width: 140,
			render: (_text: string, record: any) => (
				<Space>
					<a
						onClick={() => {
							setSelectedNotification(record);
							setNotificationsModalVisible(true);
						}}
					>
						<Tooltip title="Edit">
							<EditOutlined />
						</Tooltip>
					</a>
					<Divider type="vertical" />
					<a>
						<Popconfirm
							placement="topRight"
							title="Delete notification?"
							onConfirm={async () => {
								await handleDeleteNotification(record.id);
							}}
							okText="Yes"
							cancelText="No"
						>
							<Tooltip title="Delete">
								<DeleteOutlined />
							</Tooltip>
						</Popconfirm>
					</a>
				</Space>
			),
		},
	];

	return (
		<PublicationsWrapper>
			{unavailable ? (
				<Alert
					type="warning"
					showIcon
					message="Publications service is not available"
					description="The backoffice proxy could not reach wingops-publications."
					style={{ marginBottom: 16 }}
				/>
			) : null}

			{!unavailable && failedSources.length > 0 ? (
				<Alert
					type="error"
					showIcon
					message={`Latest crawl failed: ${failedSources.map(sourceLabel).join(', ')}`}
					description="Open Crawl runs for the error text. Source sites may have changed markup, or the lambda browser failed to launch."
					style={{ marginBottom: 16 }}
				/>
			) : null}

			<Tabs type="card" defaultActiveKey="publications">
				<TabPane tab={`Publications (${publicationsTotal})`} key="publications">
					<Toolbar>
						<Button icon={<PlusOutlined />} onClick={() => setPublicationModalVisible(true)} type="primary">
							New Publication
						</Button>
						<ToolbarRight>
							<Select
								value={publicationSource}
								onChange={setPublicationSource}
								options={SOURCE_OPTIONS}
								style={{ width: 160 }}
							/>
							<Search
								placeholder="Search publications"
								allowClear
								onSearch={(value) => setPublicationSearch(value)}
								style={{ width: 240 }}
							/>
						</ToolbarRight>
					</Toolbar>
					<Table
						id="publications"
						pagination={{
							size: 'default',
							pageSize: 50,
							current: publicationsPage,
							onChange: (page) => setPublicationsPage(page),
							total: publicationsTotal,
							showSizeChanger: false,
							showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
						}}
						locale={{ emptyText: 'No publications found' }}
						style={{ paddingBottom: 24 }}
						loading={loading}
						rowKey={(record: any) => record.id}
						columns={publicationColumns}
						dataSource={publications}
					/>
					<PublicationModal
						visible={publicationModalVisible}
						onClose={() => setPublicationModalVisible(false)}
						onSave={handleSavePublication}
						afterClose={() => setSelectedPublication({})}
						saving={savingPublication}
						publication={selectedPublication}
					/>
					<Modal
						bodyStyle={{ padding: 12 }}
						visible={showPreview}
						centered={true}
						style={{ top: 12 }}
						afterClose={() => {
							setSelectedPublication({});
							document.body.style.overflow = 'auto';
						}}
						destroyOnClose={true}
						title={selectedPublication.name || 'Preview file'}
						onCancel={() => setShowPreview(false)}
						footer={[
							<Button key="close" onClick={() => setShowPreview(false)}>
								Cose
							</Button>,
						]}
						width={'80%'}
					>
						<div style={{ textAlign: 'center' }}>
							<Tabs>
								{selectedPublication?.pdf_s3_keys?.map((s3Key: string) => (
									<TabPane tab={getPublicationFileName(s3Key)} key={`preview-${s3Key}`}>
										{
											<iframe
												style={{ width: '100%', border: 'none', height: 'calc(100vh - 240px)' }}
												src={`${process.env.API_URL}/api/backoffice/publications/${selectedPublication.id}/download?file=${s3Key}`}
												id="pdf_content"
											>
												<p>Not found...</p>
											</iframe>
										}
									</TabPane>
								))}
							</Tabs>
						</div>
					</Modal>
				</TabPane>
				<TabPane tab="Crawl runs" key="runs">
					<Toolbar>
						<ToolbarRight>
							<Select
								value={crawlRunsSource}
								onChange={setCrawlRunsSource}
								options={SOURCE_OPTIONS}
								style={{ width: 160 }}
							/>
						</ToolbarRight>
					</Toolbar>
					<Table
						id="crawl-runs"
						pagination={{
							size: 'default',
							pageSize: 50,
							current: crawlRunsPage,
							onChange: (page) => setCrawlRunsPage(page),
							total: crawlRunsTotal,
							showSizeChanger: false,
							showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
						}}
						locale={{ emptyText: 'No crawl runs found' }}
						style={{ paddingBottom: 24 }}
						loading={loading}
						rowKey={(record: any) => record.id}
						columns={crawlRunColumns}
						dataSource={crawlRuns}
						expandable={{
							rowExpandable: (record: any) => Boolean(record.error_message),
							expandedRowRender: (record: any) => (
								<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{record.error_message}</pre>
							),
						}}
					/>
				</TabPane>
				<TabPane tab={`Notifications (${notifications.length})`} key="notifications">
					<Toolbar>
						<Button
							icon={<PlusOutlined />}
							onClick={() => setNotificationsModalVisible(true)}
							type="primary"
						>
							New Notification
						</Button>
					</Toolbar>
					<Table
						id="notifications"
						pagination={{
							size: 'default',
							pageSize: 50,
							showSizeChanger: false,
							showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
						}}
						locale={{ emptyText: 'No notifications found' }}
						style={{ paddingBottom: 24 }}
						loading={loading}
						rowKey={(record: any) => record.id}
						columns={notificationColumns}
						dataSource={notifications}
					/>
					<NotificationModal
						visible={notificationsModalVisible}
						onClose={() => setNotificationsModalVisible(false)}
						onSave={handleSaveNotification}
						afterClose={() => setSelectedNotification({})}
						saving={savingNotification}
						notification={selectedNotification}
					/>
				</TabPane>
			</Tabs>
		</PublicationsWrapper>
	);
}

export default withRouter(Publications);
